import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { createBambiBaseQuery, type BambiBaseQueryConfig } from "./baseQuery.ts";
import { tagTypes } from "../generated/tagTypes.gen.ts";

type BambiBaseQuery = BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError>;

let currentBaseQuery: BambiBaseQuery | null = null;

const lazyBaseQuery: BambiBaseQuery = (args, api, extraOptions) => {
  if (!currentBaseQuery) {
    throw new Error(
      "@bambi/api-client: configureBambiApiClient(...) must be called " +
        "before any RTK-Query hook is dispatched.",
    );
  }
  return currentBaseQuery(args, api, extraOptions);
};

export const bambiApi = createApi({
  // Distinct from the legacy `frontend/.../emptyApi.ts` slice (default
  // "api"), so consumers can mount both reducers/middleware in the
  // same store during the Phase 1 coexistence period.
  reducerPath: "bambiApiClient",
  baseQuery: lazyBaseQuery,
  endpoints: () => ({}),
  tagTypes,
});

/**
 * Configure the SDK with the consumer's auth/org wiring. Idempotent —
 * call again to replace the current configuration. Each consumer should
 * call this once at app bootstrap; tests typically call it in a global
 * `beforeEach`.
 */
export function configureBambiApiClient(config: BambiBaseQueryConfig): void {
  currentBaseQuery = createBambiBaseQuery(config);
}

/**
 * Clear the SDK's configuration. If a `dispatch` is supplied, also
 * dispatches `bambiApi.util.resetApiState()` so the RTK-Query cache
 * doesn't leak between tests.
 */
export function resetBambiApiClient(dispatch?: (action: unknown) => unknown): void {
  currentBaseQuery = null;
  dispatch?.(bambiApi.util.resetApiState());
}
